import { Socket } from "phoenix";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addNotification,
  markAsRead,
  setConnectionStatus,
} from "../redux/notifications/notifications.actions";

const WEBSOCKET_URL =
  process.env.REACT_APP_WEBSOCKET_URL || "ws://localhost:4000/socket";

/**
 * Custom hook for WebSocket connection and real-time notifications
 * @returns {Object} WebSocket state and methods
 */
const useWebSocket = () => {
  const dispatch = useDispatch();
  const socketRef = useRef(null);
  const channelRef = useRef(null);
  const reconnectAttempts = useRef(0);

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  const currentUser = useSelector((state) => state.user.userData);
  const token = useSelector((state) => state.user.token);
  const userId = currentUser?._id;

  /**
   * Connect to WebSocket server
   */
  const connect = useCallback(() => {
    console.log("[WebSocket] connect() invoked", {
      hasToken: !!token,
      hasUserId: !!userId,
      timestamp: new Date().toISOString(),
    });

    if (!token || !userId) {
      console.log("[WebSocket] No token or user, skipping connection");
      return;
    }

    if (socketRef.current?.isConnected()) {
      console.log("[WebSocket] Already connected");
      return;
    }

    try {
      console.log("[WebSocket] Creating Phoenix socket", {
        userId,
      });
      // Create Phoenix socket
      const socket = new Socket(WEBSOCKET_URL, {
        params: { token },
        reconnectAfterMs: (tries) => Math.min(tries * 1000, 10000),
        heartbeatIntervalMs: 30000,
      });

      socket.onOpen(() => {
        console.log("[WebSocket] Connected");
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
        dispatch(setConnectionStatus("connected"));
      });

      socket.onClose(() => {
        console.log("[WebSocket] Disconnected");
        setIsConnected(false);
        dispatch(setConnectionStatus("disconnected"));
      });

      socket.onError((err) => {
        console.error("[WebSocket] Error:", err);
        setError("Connection error");
        dispatch(setConnectionStatus("error"));
      });

      console.log("[WebSocket] Initiating socket.connect()");
      socket.connect();
      socketRef.current = socket;

      // Join notification channel
      const channel = socket.channel(`notifications:${userId}`, {});

      channel
        .join()
        .receive("ok", () => {
          console.log(`[WebSocket] Joined notifications:${userId}`);
        })
        .receive("error", (err) => {
          console.error("[WebSocket] Failed to join channel:", err);
          setError("Failed to join notification channel");
        });

      // Listen for new notifications
      channel.on("new_notification", (notification) => {
        console.log("[WebSocket] New notification:", notification);
        dispatch(addNotification(notification));

        // Show browser notification if permitted
        showBrowserNotification(notification);
      });

      channelRef.current = channel;
    } catch (err) {
      console.error("[WebSocket] Failed to connect:", err);
      setError(err.message);
    }
  }, [token, userId, dispatch]);

  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    if (channelRef.current) {
      channelRef.current.leave();
      channelRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setIsConnected(false);
    dispatch(setConnectionStatus("disconnected"));
  }, [dispatch]);

  /**
   * Mark notifications as read
   */
  const markNotificationsRead = useCallback(
    (notificationIds) => {
      if (!channelRef.current) {
        console.warn("[WebSocket] Channel not connected");
        return;
      }

      channelRef.current
        .push("mark_read", { notification_ids: notificationIds })
        .receive("ok", (response) => {
          console.log("[WebSocket] Marked as read:", response);
          dispatch(markAsRead(notificationIds));
        })
        .receive("error", (err) => {
          console.error("[WebSocket] Failed to mark as read:", err);
        });
    },
    [dispatch],
  );

  /**
   * Send a ping to keep connection alive
   */
  const ping = useCallback(() => {
    if (!channelRef.current) return;

    channelRef.current
      .push("ping", {})
      .receive("ok", () => console.log("[WebSocket] Pong received"))
      .receive("error", () => console.warn("[WebSocket] Ping failed"));
  }, []);

  /**
   * Show browser notification
   */
  const showBrowserNotification = (notification) => {
    if (Notification.permission === "granted") {
      new Notification("Loga", {
        body: notification.message,
        icon: "/logo192.png",
        tag: notification.id,
      });
    }
  };

  /**
   * Request browser notification permission
   */
  const requestNotificationPermission = useCallback(async () => {
    if ("Notification" in window && Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }
    return Notification.permission === "granted";
  }, []);

  // Auto-connect when user logs in
  useEffect(() => {
    if (!token || !userId) {
      disconnect();
      return;
    }

    connect();

    return () => {
      disconnect();
    };
  }, [token, userId, connect, disconnect]);

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, [requestNotificationPermission]);

  return {
    isConnected,
    error,
    connect,
    disconnect,
    markNotificationsRead,
    ping,
    requestNotificationPermission,
  };
};

export default useWebSocket;
